import { encode } from 'turbo-stream';
import { uploadLimitLabel } from '~/lib/upload-limits';

/**
 * Remix reads every `.data` response as a turbo-stream, and hhOS always answers
 * one with `text/x-script`. Anything else came from outside the app: Google's
 * front end (413 for a body over 32 MiB, 502/503/504), the session gate in
 * `server.js`, or a dropped connection. Remix can't decode those, throws
 * "Unable to decode turbo-stream response", and the nearest error boundary
 * replaces the whole page, losing whatever the operator typed (HHOS-2595).
 *
 * This wraps `fetch` so a failed save shows a toast and comes back to Remix as
 * "no data" with the failure's status. Because the status is 400 or more,
 * Remix (with single fetch) also skips reloading the page's data afterwards,
 * which would hit the same failure. The page and the form stay put.
 * An expired session reloads the page, which lands on sign-in and comes back.
 * Page loads (GET) are left alone: Remix needs real loader data for those.
 */
export type SafeDataFetchOptions = {
  origin: string;
  notify: (title: string, description: string) => void;
  reload: () => void;
};

const TURBO_STREAM_CONTENT_TYPE = 'text/x-script';

// `{ data: null }` is how Remix's single fetch encodes an action that returned
// nothing; turbo-stream is pinned to the version @remix-run/react decodes with.
function failedSave(status: number): Response {
  return new Response(encode({ data: null }), {
    status: status >= 400 ? status : 502,
    headers: { 'Content-Type': TURBO_STREAM_CONTENT_TYPE },
  });
}

function requestUrl(input: RequestInfo | URL, origin: string): URL | null {
  try {
    if (input instanceof URL) return input;
    return new URL(typeof input === 'string' ? input : input.url, origin);
  } catch {
    return null;
  }
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
  return method.toUpperCase();
}

export function saveFailureDescription(status: number): string {
  if (status === 413) {
    return `The photos and videos are over ${uploadLimitLabel()} together. Remove some, save, then add the rest`;
  }
  return `hhOS didn't answer (error ${status}). Your changes are still here, so try again in a moment`;
}

const OFFLINE_DESCRIPTION =
  "Couldn't reach hhOS. Your changes are still here, so check your connection and try again";

function isSignInRedirect(res: Response, origin: string): boolean {
  if (!res.redirected) return false;
  const url = requestUrl(res.url, origin);
  return !!url && url.origin === origin && url.pathname === '/login';
}

export function createSafeDataFetch(
  baseFetch: typeof fetch,
  { origin, notify, reload }: SafeDataFetchOptions
): typeof fetch {
  return async (input, init) => {
    const url = requestUrl(input, origin);
    const isOwnDataRequest = !!url && url.origin === origin && url.pathname.endsWith('.data');
    if (!isOwnDataRequest) return baseFetch(input, init);

    const isSave = requestMethod(input, init) !== 'GET';

    let res: Response;
    try {
      res = await baseFetch(input, init);
    } catch (error) {
      // Remix aborts superseded requests on purpose; those must still reject.
      if (!isSave || (error instanceof DOMException && error.name === 'AbortError')) throw error;
      notify('Failed to save', OFFLINE_DESCRIPTION);
      return failedSave(503);
    }

    if (res.headers.get('Content-Type')?.includes(TURBO_STREAM_CONTENT_TYPE)) return res;

    if (res.status === 401 || isSignInRedirect(res, origin)) {
      reload();
      // The page is unloading; settling would only race the reload.
      return new Promise<Response>(() => {});
    }

    if (!isSave) return res;

    notify('Failed to save', saveFailureDescription(res.status));
    return failedSave(res.status);
  };
}
